import './styles/main.css';
import './styles/sections.css';
import { StudioScene } from './scenes/studio';
import type { QualityTier } from './scenes/studio';
import { BACKGROUND_IDS, isBackgroundId } from './scenes/backgrounds';
import type { BackgroundId } from './scenes/backgrounds';
import { createRouter } from './pages/router';
import { mountNav } from './pages/nav';

/**
 * `?quality=mobile|desktop` forces a tier so the mobile look can be tuned in a
 * full-size window, where the dev panel doesn't cover the viewport. Appearance
 * only — the canvas inset scales with viewport *height*, so anything geometric
 * (`cellSize`, `cellCountX`) still has to be confirmed at a real mobile size.
 */
function devQualityOverride(params: URLSearchParams): QualityTier | undefined {
  if (!import.meta.env.DEV) return undefined;
  const q = params.get('quality');
  if (q === null) return undefined;
  if (q === 'mobile' || q === 'desktop') return q;
  // eslint-disable-next-line no-console
  console.warn(`[main] ?quality=${q} — expected 'mobile' or 'desktop'; using auto-detect`);
  return undefined;
}

/**
 * `?bg=<id>` mounts a background candidate instead of the shipped default.
 * Read once at construction like `?quality=`, so changing it needs a reload.
 */
function devBackgroundOverride(params: URLSearchParams): BackgroundId | undefined {
  if (!import.meta.env.DEV) return undefined;
  const id = params.get('bg');
  if (id === null) return undefined;
  if (isBackgroundId(id)) return id;
  // eslint-disable-next-line no-console
  console.warn(`[main] ?bg=${id} — unknown; available: ${BACKGROUND_IDS.join(', ')}`);
  return undefined;
}

async function bootstrap(): Promise<void> {
  const params = new URLSearchParams(location.search);

  const canvas = document.getElementById('atmosphere');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error('atmosphere canvas missing from DOM');
  }

  if (import.meta.env.DEV && params.has('inspect')) {
    const id = params.get('inspect');
    const { MODELS } = await import('./assets/models');
    if (id && id in MODELS) {
      const { ModelInspectorScene } = await import('./scenes/modelInspector');
      const entry = MODELS[id as keyof typeof MODELS];
      const inspector = new ModelInspectorScene({ canvas, entry });
      await inspector.init();
      inspector.start();
      return;
    }
    // eslint-disable-next-line no-console
    console.warn(`[main] ?inspect=${id} — unknown model id; available: ${Object.keys(MODELS).join(', ')}`);
  }

  const app = document.getElementById('app');
  if (!(app instanceof HTMLElement)) {
    throw new Error('#app missing from DOM');
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const studio = new StudioScene({
    canvas,
    quality: devQualityOverride(params),
    background: devBackgroundOverride(params),
    reducedMotion,
    onLayoutChange: gridWidthPx => {
      document.documentElement.style.setProperty('--scene-width', `${Math.round(gridWidthPx)}px`);
    },
  });
  await studio.init();
  studio.start();

  mountNav('horizontal');
  createRouter(app, studio);
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      void bootstrap();
    },
    { once: true },
  );
} else {
  void bootstrap();
}
